/**
 * A composable that provides an animation state store.
 *
 * The store is a simple key-value store where the key is a string and the value is a GSAP timeline.
 *
 * The composable provides methods to set, get, play, pause, reverse, restart, and kill timelines.
 *
 * @example
 *  const { getTimeline } = useAnimationState()
 *  <button @click="getTimeline('rotate')?.reverse()">Rotate data</button>
 */

export type AnimationItem = { tl: gsap.core.Timeline; setup: () => gsap.core.Timeline }

export type Animation = Record<string, AnimationItem>

export function useAnimationState() {
  const timelines = useState<Animation>('timelines', () => ({}))

  const setTimeline = (key: string, animation: AnimationItem) => {
    if (!key || typeof key !== 'string') {
      console.warn('[useAnimationStore] Invalid key passed to setTimeline')
      return
    }

    if (!animation.tl || typeof animation.tl.play !== 'function') {
      console.warn(`[useAnimationStore] Invalid timeline provided for key "${key}"`)
      return
    }

    timelines.value[key] = animation

    return animation
  }

  const getTimeline = (key: string): gsap.core.Timeline | undefined => {
    if (!key || typeof key !== 'string') {
      console.warn('[useAnimationStore] Invalid key passed to getTimeline')
      return undefined
    }

    const timeline = timelines.value[key]
    if (!timeline) {
      console.warn(`[useAnimationStore] Timeline "${key}" not found`)
    }

    return timeline?.tl
  }

  const playTimeline = (key: string) => {
    const animation = getTimeline(key)
    animation?.play?.()
  }

  const pauseTimeline = (key: string) => {
    const animation = getTimeline(key)
    animation?.pause?.()
  }

  const reverseTimeline = (key: string) => {
    const animation = getTimeline(key)
    animation?.reverse?.()
  }

  const restartTimeline = (key: string) => {
    const animation = getTimeline(key)
    animation?.restart?.()
  }

  const killTimeline = (key: string) => {
    const animation = getTimeline(key)
    if (animation) {
      animation?.kill()

      if (timelines.value[key]) {
        delete timelines.value[key]
      }
    }
  }

  const hasTimeline = (key: string): boolean => {
    return !!timelines.value[key]
  }

  function refreshAll() {
    for (const id in timelines.value) {
      const animation = timelines.value[id]
      if (!animation) continue

      animation.tl.kill()
      animation.tl = animation?.setup()
    }
  }

  return {
    refreshAll,
    timelines,
    setTimeline,
    getTimeline,
    playTimeline,
    pauseTimeline,
    reverseTimeline,
    restartTimeline,
    killTimeline,
    hasTimeline,
  }
}
